import { TRImage } from '@front-app/store/util/image';
import { Community } from '../CommunityApi/CommunityModel';
import { PublicItem } from '../SharedModels';
import { UserRole } from '../UserApi/UserModel';
import { GroupAccessSettings } from './GroupAccessSettingsModel';
import { VisibilityOption } from './GroupEvent';

export const enum PreferenceToken {
	// Members can view group Main tab
	HEADLINE_VIEW = 'headlineView',
	// Members can post discussions
	DISCUSSION_WRITE = 'discussionWrite',
	DISCUSSION_VIEW = 'discussionView',
	DISCUSSION_LIST_VIEW = 'discussionListView',
	// Custom group page
	CUSTOM_PAGE = 'viewPage',
	// Admins can post discussions
	DISCUSSION_ADMIN_WRITE = 'discussionAdminWrite',
	// User can add/remove the categories in the discussion
	MANAGE_DISCUSSION_CATEGORY = 'manageDiscussionCategory',
	// User can leave the group
	LEAVE_LINK = 'leaveLink',
	// User should see a warning before leaving group
	LEAVE_LINK_WARN = 'leaveLinkWarn',
	// User can see a limited preview in a private group
	PRIVATE_GROUP_PREVIEW = 'privateGroupPreview',
	// User should see a join button in the group
	JOIN_LINK = 'joinLink',
	// Highlights
	VIEW_HIGHLIGHTS = 'viewWidget',
	// Custom page
	EDIT_CUSTOM_PAGE = 'editPage',
	// Can the user see the group category widget at all
	CATEGORY_WIDGET = 'categoryWidget',
	// Can the user see the group member list widget at all
	MEMBER_WIDGET_VIEW = 'memberWidgetView',
	// Can the user see the member list for user types:
	MEMBER_LIST_VIEW = 'memberListView',
	ADMIN_LIST_VIEW = 'administratorRoleMemberListView',
	MONITOR_LIST_VIEW = 'moderatorRoleMemberListView',
	INVITEE_LIST_VIEW = 'inviteeRoleMemberListView',
	DEPARTEE_LIST_VIEW = 'departeeRoleMemberListView',
	EXTERNAL_INVITEE_LIST_VIEW = 'externalInviteMemberListView',
	// An override to allow CSRs to see invitees, etc.
	ADMIN_PAGE_VIEW = 'adminPageView',
	EDIT_NOTIFICATION = 'editNotification',
	DISCUSSION_MODERATOR_NOTIFY = 'discussionModeratorNotify',
	FILE_MODERATOR_NOTIFY = 'fileModeratorNotify',
	// Member management
	MANAGE_MEMBERS='groupManageMembers',
	INVITE_MEMBERS='inviteToGroup',
	INVITE_MONITORS='inviteModerators',
	INVITE_ADMINISTRATORS='inviteAdministrators',
	EXTERNAL_INVITE_MEMBERS='groupExternalInvite',
	EXTERNAL_BULK_INVITE_MEMBERS='groupExternalBulkInvite',
	// Files
	UPLOAD_FILE = 'uploadFile',
	REMOVE_FILE = 'removeGroupFile',
	FILE_VIEW = 'fileView',
	FILE_LIST_VIEW = 'fileListView',
	MANAGE_DOCUMENT_CATEGORY = 'manageDocumentCategory',
	VIEW_EVENT = 'viewEvent',
	CREATE_EVENT = 'createEvent',
	EDIT_EVENT = 'editEvent',
	// LMS
	ENABLE_LMS = 'enableLms',
	ENHANCED_DATA_DASHBOARDS = 'enhancedDataDashboards',
	// Category Management
	MANAGE_GROUP_CATEGORY = 'manageGroupCategory',
	// Group Gating
	/**
	 * @deprecated - This will be enforced by the API for all users without access in future
	 */
	LIMITED_ACCESS_MESSAGE = 'limitedAccessMessage',
	// The user must agree to the terms and conditions (either because the user is new to
	// the group/event and they are required, or because the terms have been updated and all
	// members must agree to the new ones)
	USER_TERMS_REQUIRED = 'userTermsRequired',
	// The user must agreed to share personal information with the group owner in order to join the group
	SHARE_PERSONAL_INFORMATION_REQUIRED = 'sharePersonalInformationRequired',
	// The user must enter Access Code in order to join the group
	ACCESS_CODE_REQUIRED = 'accessCodeRequired',
	// The user must add a "professional" role to their user account to access the group,
	// but has not yet done so
	PROFESSIONAL_ROLE_REQUIRED = 'professionalRoleRequired',
	// The user must get their professional role verified in order to access the group,
	// but has not yet done so
	PROFESSIONAL_ROLE_VERIFICATION_REQUIRED = 'professionalRoleVerificationRequired',
}

export const enum GroupServiceLevel {
	AUTO = 'group.profile.default.auto',
	STANDARD = 'group.profile.default.2',
	PREMIUM_LITE = 'group.profile.default.3',
	PREMIUM = 'group.profile.default.4',
	EVENT = 'group.profile.default.event',
	CUSTOM = 'group.profile.custom',
}

export interface AdminSettings {
	// If true, users can notify all members when posting a discusionn
	notifyAllMembersDiscussionEnabled: boolean;
	// The default value for the "notify all members" checkbox
	notifyAllMembersDiscussionDefault: boolean;
	notifyAllMembersFileEnabled: boolean;
	notifyAllMembersFileDefault: boolean;
	notifyModeratorsAllDiscussions: boolean;
	notifyModeratorsNewFiles: boolean;
}

export interface VisibilitySettings {
	visibility: VisibilityOption;
}

export interface Group {
	// Hashed ID
	id: string;
	name: string;
	description: string;
	// Group type header, displays resource bundle key
	type: GroupServiceLevel;
	// User role in relation to group
	role: UserRole;
	// logo image source
	logoSrc: string;
	logo: TRImage;
	// banner image source
	bannerSrc: string;
	banner: TRImage;
	// Base64 encoded link associated with the banner image
	bannerLink: string;
	// HTML storage, Base64 encoded
	widgetHeaderHtml: string;
	// HTML storage, Base64 encoded
	widgetHtml: string;
	// HTML storage, Base64 encoded. Does not indicate if terms are required
	termsAndConditionsHtml: string;
	// Indicates if members need to accept or renew the terms of use
	termsRequired: boolean;
	// Indicates if members need to make a payment
	paymentRequired: boolean;
	created: Date;
	categories: Array<string>;
	/**
	 * GUI display tokens. Includes tokens for TOU and paywall (userTermsRequired
	 * and userPayRequired). Present if a user has not accepted the required terms
	 * of use or made required payment (see termsRequired and paymentRequired)
	 */
	preferenceTokens: Array<PreferenceToken>;
	version: number;
	lastVisited: number;
	pageHtml: string;
	pageName: string;
	numMembers: number;
	conditions: GroupConditions;
	// Number of used bytes in file upload quota
	totalFileBytes: number;
	accessSettings: GroupAccessSettings | null;
	adminSettings: AdminSettings;
	visibilitySettings: VisibilitySettings;
}

export interface GroupWithCommunity extends Group {
	community: Community | null;
}

export interface PublicGroup {
	// Hashed ID
	groupId: string;
	name: string;
	categories: string[];
	description: string;
	visibility: VisibilityOption;
}

export interface GroupConditions {
	maxMembers: number;
	maxModerators: number;
	maxAdministrators: number;
	maxUploadFileSize: number;
	maxTotalUploadFileSize: number;
}

export type Groups = GroupWithCommunity[];

export interface GroupStats {
	id: string;
	numDiscussions: number;
	numDiscussionPosts: number;
	numActiveMembers: number;
	numInviteesInternal: number;
	numInviteesExternal: number;
	numSharedItems: number;
	numActiveEvents: number;
	numPastEvents: number;
	numFutureEvents: number;
}

export type PublicGroupData = PublicItem<PublicGroup>;
